import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Copy, MoreVertical, Link, Package, FileText, X, Check, ArrowUpDown, CreditCard, FolderPlus, ExternalLink, ImageIcon, LayoutGrid, List, Loader2, Camera, Upload, Star, BarChart3, Filter, CalendarDays } from 'lucide-react';
import { productsApi } from '../../api/endpoints/products';
import { paylinksApi } from '../../api/endpoints/paylinks';
import { categoriesApi } from '../../api/endpoints/categories';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/SearchInput';
import { Select } from '../../components/ui/Select';
import { FilterDropdown } from '../../components/ui/FilterDropdown';
import { Badge } from '../../components/ui/Badge';
import { CurrencyDisplay, SarIcon } from '../../components/ui/CurrencyDisplay';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { Modal } from '../../components/ui/Modal';
import { PortalDropdown } from '../../components/ui/PortalDropdown';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { rejectReasonFor, totalUploadBytes, MAX_PRODUCT_IMAGES, MAX_UPLOAD_BYTES } from '../../lib/imageUpload';
import { useToast } from '../../components/ui/Alert';
import { QuotationModal } from '../../components/ui/QuotationModal';
import { Tag } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/ui/Pagination';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { getCdnUrl, getStoreUrl } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import { ProductStatsModal } from './ProductStatsModal';
import { useProductForm } from '../../contexts/ProductFormContext';
import { QuickPaylinkModal } from '../../components/paylinks/QuickPaylinkModal';
import type { Product } from '../../types/product';

const SORT_OPTIONS = [
  { value: 'createdAt:desc', labelKey: 'products.newestFirst' },
  { value: 'createdAt:asc', labelKey: 'products.oldestFirst' },
  { value: 'price:desc', labelKey: 'products.priceHighToLow' },
  { value: 'price:asc', labelKey: 'products.priceLowToHigh' },
] as const;

export function ProductList() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();

  const [viewMode, setViewMode] = useState<'grid' | 'table'>(() => (localStorage.getItem('products_view') as 'grid' | 'table') || 'grid');
  const setView = (mode: 'grid' | 'table') => {
    setViewMode(mode);
    localStorage.setItem('products_view', mode);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const debouncedSearch = useDebounce(search);
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sortValue, setSortValue] = useState('createdAt:desc');
  const { page, limit, goToPage } = usePagination({
    resetDeps: [debouncedSearch, typeFilter, categoryFilter, dateFilter, sortValue],
  });
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Trigger element for the currently-open row/card actions menu. The menu is
  // portalled and positioned against this, so it can't expand the grid/table
  // scroll area the way an absolutely-positioned dropdown does.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Inline edit
  const [editingCell, setEditingCell] = useState<{ id: string; field: 'price' | 'quantity' | 'title' } | null>(null);
  const [editValue, setEditValue] = useState('');

  // Quick category creation
  const [showCategoryCreate, setShowCategoryCreate] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');

  // Quick paylink modal
  const [paylinkTarget, setPaylinkTarget] = useState<Product | null>(null);
  const [paylinkTitle, setPaylinkTitle] = useState('');
  const [paylinkQty, setPaylinkQty] = useState(1);

  // Quotation modal
  const [showQuotation, setShowQuotation] = useState(false);

  // Standalone Quick Paylink modal
  const [showQuickPaylink, setShowQuickPaylink] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);

  // Product create/edit modal is owned by ProductFormProvider so it can be opened
  // from the bot or other pages too. Local helpers preserve the original API shape.
  const { openCreate, openEdit } = useProductForm();
  const setFormProductId = useCallback((value: string | null | undefined) => {
    if (value === undefined) openCreate();
    else if (typeof value === 'string') openEdit(value);
    // value === null is "close", which the provider handles via its onClose
  }, [openCreate, openEdit]);

  // Card inline editing state
  const [cardEdits, setCardEdits] = useState<Map<string, { title?: string; price?: number; salePrice?: number; quantity?: number | null; categories?: string[] }>>(new Map());
  const [cardSaving, setCardSaving] = useState<Set<string>>(new Set());
  const [savedCards, setSavedCards] = useState<Set<string>>(new Set());
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const savedTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [imageUploading, setImageUploading] = useState<Set<string>>(new Set());
  const [imagePopupProduct, setImagePopupProduct] = useState<Product | null>(null);
  const [statsProduct, setStatsProduct] = useState<Product | null>(null);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState<string | null>(null);

  const updateCardField = useCallback((productId: string, field: string, value: unknown) => {
    setCardEdits(prev => {
      const next = new Map(prev);
      const existing = next.get(productId) || {};
      next.set(productId, { ...existing, [field]: value });
      return next;
    });
  }, []);

  const isCardDirty = useCallback((productId: string, product?: Product) => {
    const edits = cardEdits.get(productId);
    if (!edits) return false;
    if (!product) return true;
    // Check if any field actually differs from the original
    for (const [key, val] of Object.entries(edits)) {
      const original = (product as unknown as Record<string, unknown>)[key];
      // Treat null/undefined/Infinity as equivalent for quantity
      if (key === 'quantity') {
        const origNull = original == null || original === Infinity;
        const editNull = val === null || val === undefined;
        if (origNull && editNull) continue;
        if (origNull !== editNull || val !== original) return true;
        continue;
      }
      // Treat 0/undefined/null as equivalent "no sale price"
      if (key === 'salePrice') {
        const origEmpty = !original || original === 0;
        const editEmpty = !val || val === 0;
        if (origEmpty && editEmpty) continue;
        if (origEmpty !== editEmpty || val !== original) return true;
        continue;
      }
      if (val !== original) return true;
    }
    return false;
  }, [cardEdits]);

  const saveCard = useCallback(async (productId: string) => {
    const edits = cardEdits.get(productId);
    if (!edits) return;
    setCardSaving(prev => { const next = new Set(prev); next.add(productId); return next; });
    try {
      // Attach original quantity for atomic $inc on backend
      const payload: Record<string, unknown> = { ...edits };
      const cached = queryClient.getQueryData<{ products: Product[] }>(['products', storeId]);
      const original = cached?.products?.find(p => p._id === productId);
      if (edits.quantity !== undefined && original) {
        payload._originalQuantity = original.quantity ?? null;
      }
      // Backend's edit schema expects either a number or the string 'Infinity'
      // for unlimited stock; null fails its union. Normalise here so both the
      // ∞ button and clearing the input send the wire format the API accepts.
      if (payload.quantity === null) {
        payload.quantity = 'Infinity';
      }
      await productsApi.update(storeId!, original?.productId || productId, payload as any);
      queryClient.invalidateQueries({ queryKey: ['products', storeId] });
      setCardEdits(prev => { const next = new Map(prev); next.delete(productId); return next; });
      // Flash success
      setSavedCards(prev => { const next = new Set(prev); next.add(productId); return next; });
      // Clear any existing timer for this card
      const existingTimer = savedTimers.current.get(productId);
      if (existingTimer) clearTimeout(existingTimer);
      const timer = setTimeout(() => {
        setSavedCards(prev => { const next = new Set(prev); next.delete(productId); return next; });
        savedTimers.current.delete(productId);
      }, 1500);
      savedTimers.current.set(productId, timer);
      toast('success', t('common.success'));
    } catch {
      toast('error', t('common.error'));
    } finally {
      setCardSaving(prev => { const next = new Set(prev); next.delete(productId); return next; });
    }
  }, [cardEdits, storeId, queryClient, toast, t]);

  const handleCardImageSwap = useCallback((product: Product) => {
    setImagePopupProduct(product);
  }, []);

  const { data: categoriesData } = useQuery({
    queryKey: ['categories', storeId],
    queryFn: () => categoriesApi.list(storeId!),
    enabled: !!storeId,
  });
  const categories = categoriesData?.categories ?? [];

  const [sortField, sortDir] = sortValue.split(':') as [string, 'asc' | 'desc'];

  const { data, isLoading } = useQuery({
    queryKey: ['products', storeId, { page, limit, search: debouncedSearch, productType: typeFilter, category: categoryFilter, date: dateFilter, sort: sortField, sortDirection: sortDir }],
    queryFn: () => productsApi.list(storeId!, {
      page, limit,
      search: debouncedSearch || undefined,
      productType: typeFilter || undefined,
      category: categoryFilter || undefined,
      date: dateFilter || undefined,
      sort: sortField,
      sortDirection: sortDir,
    }),
    enabled: !!storeId,
  });

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget || !storeId) return;
    setDeleting(true);
    try {
      await productsApi.delete(storeId, deleteTarget.productId || deleteTarget._id);
      queryClient.invalidateQueries({ queryKey: ['products', storeId] });
      toast('success', `${t('products.deleteSuccess')}: ${deleteTarget.title}`);
      setDeleteTarget(null);
    } catch {
      toast('error', t('common.error'));
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, storeId, queryClient, toast, t]);

  const duplicateMutation = useMutation({
    mutationFn: (productId: string) => productsApi.duplicate(storeId!, productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', storeId] });
      toast('success', t('products.duplicateSuccess'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const inlineUpdateMutation = useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: Record<string, unknown> }) =>
      productsApi.update(storeId!, productId, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', storeId] });
      toast('success', t('common.success'));
      setEditingCell(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const paylinkMutation = useMutation({
    mutationFn: ({ productId, title, qty }: { productId: string; title: string; qty: number }) =>
      paylinksApi.create(storeId!, {
        products: { [productId]: { defaultQuantity: qty } },
        title: title || t('products.paylinkForProduct'),
      }),
    onSuccess: (data) => {
      const link = `${getStoreUrl(currentStore, storeId)}/paylink/${data.link || data.paylinkId}`;
      navigator.clipboard.writeText(link).catch(() => {});
      toast('success', t('paylinks.linkCopied'));
      setPaylinkTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const categoryCreateMutation = useMutation({
    mutationFn: (input: { name: string; description?: string; parentId?: string }) =>
      categoriesApi.create(storeId!, {
        name: input.name,
        description: input.description || undefined,
        parentId: input.parentId || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', storeId] });
      toast('success', t('categories.createSuccess', 'Category created'));
      setShowCategoryCreate(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
      setNewCategoryParentId('');
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const targets = products.filter((p) => selectedIds.has(p._id));
      for (const p of targets) {
        await productsApi.delete(storeId!, p.productId || p._id);
      }
      queryClient.invalidateQueries({ queryKey: ['products', storeId] });
      toast('success', t('products.deleteSuccess'));
      setSelectedIds(new Set());
    } catch {
      toast('error', t('common.error'));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleInlineSave = (rowId: string, field: 'price' | 'quantity' | 'title') => {
    const product = data?.products?.find(p => p._id === rowId);
    const apiId = product?.productId || rowId;
    if (field === 'title') {
      const trimmed = editValue.trim();
      if (!trimmed) return;
      inlineUpdateMutation.mutate({ productId: apiId, data: { title: trimmed } });
      return;
    }
    const val = Number(editValue);
    if (isNaN(val) || val < 0) return;
    const payload: Record<string, unknown> = { [field]: val };
    if (field === 'quantity' && product) {
      payload._originalQuantity = product.quantity ?? null;
    }
    inlineUpdateMutation.mutate({ productId: apiId, data: payload });
  };

  const columns: Column<Product>[] = [
    {
      key: 'image',
      header: t('common.image'),
      className: 'w-20',
      render: (p) => (
        <button
          type="button"
          onClick={() => setFormProductId(p.productId || p._id)}
          className="group relative block h-16 w-16 shrink-0 cursor-pointer overflow-hidden rounded-lg"
          title={t('common.clickToEdit', 'Click to edit')}
        >
          {p.images?.[0] ? (
            <img src={getCdnUrl(p.images[0], currentStore?._id)} alt="" className="h-16 w-16 rounded-lg object-cover" />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-bg-tertiary" />
          )}
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-colors group-hover:bg-black/40">
            <ImageIcon className="h-5 w-5 text-white opacity-0 transition-opacity group-hover:opacity-100" />
          </div>
        </button>
      ),
    },
    {
      key: 'title',
      header: t('common.name'),
      sortable: true,
      render: (p) =>
        editingCell?.id === p._id && editingCell.field === 'title' ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleInlineSave(p._id, 'title'); if (e.key === 'Escape') setEditingCell(null); }}
              autoFocus
              className="w-40 rounded border border-accent bg-bg-field px-2 py-1 text-sm outline-none"
            />
            <button onClick={() => handleInlineSave(p._id, 'title')} className="rounded p-0.5 text-success hover:bg-bg-hover"><Check className="h-3.5 w-3.5" /></button>
            <button onClick={() => setEditingCell(null)} className="rounded p-0.5 text-text-muted hover:bg-bg-hover"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { setEditingCell({ id: p._id, field: 'title' }); setEditValue(p.title); }}
              className="font-medium text-start hover:text-accent transition-colors cursor-pointer"
              title={t('common.clickToEdit', 'Click to edit')}
            >
              {p.title}
            </button>
          </div>
        ),
    },
    {
      key: 'price',
      header: t('common.price'),
      sortable: true,
      render: (p) =>
        editingCell?.id === p._id && editingCell.field === 'price' ? (
          <div className="flex items-center gap-1">
            <input
              type="number"
              step="0.01"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleInlineSave(p._id, 'price'); if (e.key === 'Escape') setEditingCell(null); }}
              autoFocus
              className="w-20 rounded border border-accent bg-bg-field px-2 py-1 text-sm outline-none"
            />
            <button onClick={() => handleInlineSave(p._id, 'price')} className="rounded p-0.5 text-success hover:bg-bg-hover"><Check className="h-3.5 w-3.5" /></button>
            <button onClick={() => setEditingCell(null)} className="rounded p-0.5 text-text-muted hover:bg-bg-hover"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <button
            onClick={() => { setEditingCell({ id: p._id, field: 'price' }); setEditValue(String(p.price)); }}
            className="cursor-pointer hover:text-accent transition-colors"
            title={t('common.clickToEdit', 'Click to edit')}
          >
            <CurrencyDisplay amount={p.price} currency={currentStore?.currency} />
          </button>
        ),
    },
    {
      key: 'type',
      header: t('common.type'),
      render: (p) => <Badge>{t(`products.${p.productType}`, p.productType)}</Badge>,
    },
    {
      key: 'quantity',
      header: t('common.quantity'),
      sortable: true,
      render: (p) =>
        p.productType === 'code' ? (
          <span className="text-text-muted" title={t('products.codesQuantity', 'Based on codes count')}>{p.quantity ?? 0}</span>
        ) : editingCell?.id === p._id && editingCell.field === 'quantity' ? (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleInlineSave(p._id, 'quantity'); if (e.key === 'Escape') setEditingCell(null); }}
              autoFocus
              className="w-16 rounded border border-accent bg-bg-field px-2 py-1 text-sm outline-none"
            />
            <button onClick={() => handleInlineSave(p._id, 'quantity')} className="rounded p-0.5 text-success hover:bg-bg-hover"><Check className="h-3.5 w-3.5" /></button>
            <button onClick={() => setEditingCell(null)} className="rounded p-0.5 text-text-muted hover:bg-bg-hover"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <button
            onClick={() => { setEditingCell({ id: p._id, field: 'quantity' }); setEditValue(String(p.quantity ?? '')); }}
            className="cursor-pointer hover:text-accent transition-colors"
            title={t('common.clickToEdit', 'Click to edit')}
          >
            {p.quantity == null || p.quantity === Infinity ? (
              <span className="text-text-muted">∞</span>
            ) : (
              <span className={p.quantity <= 0 ? 'text-danger font-medium' : ''}>{p.quantity}</span>
            )}
          </button>
        ),
    },
    {
      key: 'accessibility',
      header: t('common.status'),
      render: (p) => {
        const variant = p.accessibility === 'public' ? 'success' : p.accessibility === 'archived' ? 'default' : ('warning' as const);
        return <Badge variant={variant}>{t(`products.${p.accessibility}`, p.accessibility)}</Badge>;
      },
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (p) => (
        <div className="flex items-center">
          <button
            onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setOpenMenuId(openMenuId === p._id ? null : p._id); }}
            className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-text-primary"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          <PortalDropdown
            anchorEl={menuAnchor}
            open={openMenuId === p._id}
            onClose={() => setOpenMenuId(null)}
            width={176}
            className="w-44 rounded-lg border border-border-default bg-bg-secondary shadow-xl"
          >
                <PermissionGate requires="products.edit">
                  <button onClick={() => { setFormProductId(p.productId || p._id); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-bg-hover">
                    <Pencil className="h-4 w-4" /> {t('common.edit')}
                  </button>
                </PermissionGate>
                <button onClick={() => { setStatsProduct(p); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-bg-hover">
                  <BarChart3 className="h-4 w-4" /> {t('products.stats', 'Stats')}
                </button>
                <button onClick={() => { window.open(`https://${currentStore?.id ?? storeId}.00stores.com/product/${p.productId || p._id}`, '_blank'); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-bg-hover">
                  <ExternalLink className="h-4 w-4" /> {t('products.viewInStore', 'View in Store')}
                </button>
                <PermissionGate requires="products.add">
                  <button onClick={() => { duplicateMutation.mutate(p.productId || p._id); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-bg-hover">
                    <Copy className="h-4 w-4" /> {t('common.duplicate')}
                  </button>
                </PermissionGate>
                {p.paylinkUrl ? (
                  <button onClick={() => { navigator.clipboard.writeText(p.paylinkUrl!); toast('success', t('common.copied', 'Copied to clipboard')); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-accent hover:bg-bg-hover">
                    <Copy className="h-4 w-4" /> {t('products.copyPaylink', 'Copy Paylink')}
                  </button>
                ) : (
                  <PermissionGate requires="paylinks.add">
                    <button onClick={() => { setPaylinkTarget(p); setPaylinkTitle(p.title); setPaylinkQty(1); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-accent hover:bg-bg-hover">
                      <Link className="h-4 w-4" /> {t('products.createPaylink')}
                    </button>
                  </PermissionGate>
                )}
                <PermissionGate requires="products.delete">
                  <button onClick={() => { setDeleteTarget(p); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-bg-hover">
                    <Trash2 className="h-4 w-4" /> {t('common.delete')}
                  </button>
                </PermissionGate>
          </PortalDropdown>
        </div>
      ),
    },
  ];

  const products = data?.products ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);
  const isEmpty = !isLoading && products.length === 0 && !debouncedSearch && !typeFilter && !categoryFilter;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold">{t('products.title')}</h1>
            {total != null && (
              <span className="rounded-full bg-bg-tertiary px-2.5 py-0.5 text-xs font-medium text-text-secondary">
                {total}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-text-secondary">{t('products.description', 'Manage your store products')}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center rounded-lg border border-border-default">
            <button
              onClick={() => setView('grid')}
              className={`flex items-center justify-center rounded-s-lg p-2 transition-colors ${
                viewMode === 'grid'
                  ? 'bg-accent text-white'
                  : 'text-text-muted hover:bg-bg-hover hover:text-text-primary'
              }`}
              title={t('common.gridView', 'Grid view')}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setView('table')}
              className={`flex items-center justify-center rounded-e-lg p-2 transition-colors ${
                viewMode === 'table'
                  ? 'bg-accent text-white'
                  : 'text-text-muted hover:bg-bg-hover hover:text-text-primary'
              }`}
              title={t('common.tableView', 'Table view')}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowQuickPaylink(true)}>
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">{t('products.quickPaylink', 'Quick Paylink')}</span>
          </Button>
          <Button onClick={() => setFormProductId(undefined)}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">{t('products.addProduct')}</span>
          </Button>
          {/* More actions dropdown */}
          <div className="relative">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowMoreActions(!showMoreActions)}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
            {showMoreActions && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMoreActions(false)} />
                <div className="absolute end-0 top-full mt-1 z-50 w-52 rounded-xl border border-border-default bg-bg-secondary p-1.5 shadow-lg">
                  <button
                    onClick={() => { navigate(`/${storeId}/products/sort`); setShowMoreActions(false); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
                  >
                    <ArrowUpDown className="h-4 w-4" />
                    {t('products.sortProducts', 'ترتيب المنتجات')}
                  </button>
                  <button
                    onClick={() => { setShowQuotation(true); setShowMoreActions(false); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                    {t('products.generateQuotation', 'عرض سعر')}
                  </button>
                  <PermissionGate requires="products.add">
                    <button
                      onClick={() => { navigate(`/${storeId}/products/import`); setShowMoreActions(false); }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      {t('productImport.menuItem', 'استيراد المنتجات')}
                    </button>
                  </PermissionGate>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-border-default bg-bg-secondary px-6 py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/10">
            <Package className="h-8 w-8 text-accent" />
          </div>
          <h2 className="mb-2 text-lg font-semibold">{t('products.emptyTitle')}</h2>
          <p className="mb-6 max-w-sm text-sm text-text-secondary">{t('products.emptyDesc')}</p>
          <div className="flex flex-col items-center gap-2 sm:flex-row">
            <Button size="lg" onClick={() => setFormProductId(undefined)}>
              <Plus className="h-5 w-5" />
              {t('products.addProduct')}
            </Button>
            <PermissionGate requires="products.add">
              <Button size="lg" variant="secondary" onClick={() => navigate(`/${storeId}/products/import`)}>
                <Upload className="h-5 w-5" />
                {t('productImport.menuItem', 'استيراد المنتجات')}
              </Button>
            </PermissionGate>
          </div>
        </div>
      ) : (
        <>
          {/* Category tabs */}
          <div className="mb-3 flex items-center gap-1 overflow-x-auto pb-0.5 scrollbar-none">
            <button
              onClick={() => setCategoryFilter('')}
              className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                !categoryFilter
                  ? 'bg-accent text-white'
                  : 'text-text-secondary hover:bg-bg-hover'
              }`}
            >
              {t('common.all')} {total > 0 && <span className="ms-0.5 opacity-70">({total})</span>}
            </button>
            {categories.map((cat) => (
              <button
                key={cat._id}
                onClick={() => setCategoryFilter(cat._id)}
                className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  categoryFilter === cat._id
                    ? 'bg-accent text-white'
                    : 'text-text-secondary hover:bg-bg-hover'
                }`}
              >
                {cat.name}
              </button>
            ))}
            <PermissionGate requires="products.edit">
              <button
                onClick={() => { setNewCategoryName(''); setNewCategoryDescription(''); setNewCategoryParentId(''); setShowCategoryCreate(true); }}
                className="shrink-0 flex items-center justify-center rounded-full px-1.5 py-1 text-text-muted transition-colors hover:text-accent hover:bg-accent/5"
                title={t('categories.addCategory', 'Add category')}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </PermissionGate>
          </div>

          {/* Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <SearchInput value={search} onChange={setSearch} placeholder={t('common.search') + '...'} className="w-full sm:w-52" />
            <FilterDropdown
              icon={Filter}
              options={[
                { value: '', label: t('products.allTypes') },
                { value: 'physical', label: t('products.physical', 'Physical') },
                { value: 'digital', label: t('products.digital', 'Digital') },
                { value: 'code', label: t('products.code', 'Code') },
                { value: 'file', label: t('products.file', 'File') },
                { value: 'service', label: t('products.service', 'Service') },
              ]}
              value={typeFilter}
              onChange={setTypeFilter}
            />
            <FilterDropdown
              icon={ArrowUpDown}
              options={SORT_OPTIONS.map(o => ({ value: o.value, label: t(o.labelKey) }))}
              value={sortValue}
              onChange={setSortValue}
            />
            <FilterDropdown
              icon={CalendarDays}
              options={[
                { value: '', label: t('common.allTime', 'All Time') },
                { value: '1d', label: t('common.today', 'Today') },
                { value: '7d', label: t('common.last7Days', 'Last 7 days') },
                { value: '4w', label: t('common.last4Weeks', 'Last 4 weeks') },
                { value: '12m', label: t('common.last12Months', 'Last 12 months') },
              ]}
              value={dateFilter}
              onChange={setDateFilter}
            />
          </div>

          {/* Bulk actions bar */}
          {selectedIds.size > 0 && (
            <div className="mb-3 flex items-center gap-3 rounded-lg border border-accent/30 bg-accent/5 px-4 py-2.5">
              <span className="text-sm font-medium">{selectedIds.size} {t('common.selected', 'selected')}</span>
              <PermissionGate requires="products.delete">
                <Button variant="danger" size="sm" onClick={handleBulkDelete} isLoading={bulkDeleting}>
                  <Trash2 className="h-3.5 w-3.5" />
                  {t('common.delete')}
                </Button>
              </PermissionGate>
              <Button variant="secondary" size="sm" onClick={() => setSelectedIds(new Set())}>
                {t('common.clearSelection', 'Clear')}
              </Button>
            </div>
          )}

          {/* Content: Grid or Table */}
          {isLoading ? (
            <LoadingSpinner fullPage />
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-border-default bg-bg-secondary px-6 py-16 text-center">
              <p className="text-sm text-text-secondary">{t('common.noResults')}</p>
              <p className="mt-1 text-xs text-text-muted">{t('common.tryDifferentSearch')}</p>
            </div>
          ) : viewMode === 'grid' ? (
            <>
              {/* Card Grid View */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                {products.map((p) => {
                  const isSelected = selectedIds.has(p._id);
                  const hasMenu = openMenuId === p._id;
                  return (
                    <div
                      key={p._id}
                      className={`group relative flex flex-col rounded-xl border bg-bg-secondary transition-all duration-200 hover:shadow-md ${
                        savedCards.has(p._id)
                          ? 'border-success ring-1 ring-success/30'
                          : isSelected
                            ? 'border-accent ring-1 ring-accent/30'
                            : 'border-border-default hover:border-accent/50'
                      }`}
                    >
                      {/* Checkbox */}
                      <div className="absolute start-2.5 top-2.5 z-[5]">
                        <label className={`flex h-5 w-5 items-center justify-center rounded border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-accent bg-accent text-white'
                            : 'border-white/70 bg-white/70 text-transparent backdrop-blur-sm opacity-0 group-hover:opacity-100'
                        }`}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const next = new Set(selectedIds);
                              if (next.has(p._id)) next.delete(p._id);
                              else next.add(p._id);
                              setSelectedIds(next);
                            }}
                            className="sr-only"
                          />
                          {isSelected && <Check className="h-3 w-3" />}
                        </label>
                      </div>

                      {/* Image */}
                      <div className="relative aspect-square w-full overflow-hidden rounded-t-xl bg-bg-tertiary">
                        <button
                          type="button"
                          onClick={() => handleCardImageSwap(p)}
                          disabled={imageUploading.has(p._id)}
                          className="relative h-full w-full cursor-pointer"
                          title={t('products.clickToChangeImage', 'Click to change image')}
                        >
                          {p.images?.[0] ? (
                            <img
                              src={getCdnUrl(p.images[0], currentStore?._id)}
                              alt={p.title}
                              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Package className="h-10 w-10 text-text-muted/40" />
                            </div>
                          )}
                          {/* Upload hover overlay */}
                          {!imageUploading.has(p._id) && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30">
                              <Camera className="h-6 w-6 text-white opacity-0 drop-shadow transition-opacity group-hover:opacity-80" />
                            </div>
                          )}
                          {/* Loading overlay */}
                          {imageUploading.has(p._id) && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <Loader2 className="h-7 w-7 animate-spin text-white" />
                            </div>
                          )}
                        </button>
                        {/* Pencil icon to open full form modal */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setFormProductId(p.productId || p._id); }}
                          className="absolute end-2 bottom-2 z-[5] flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-white opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/70 group-hover:opacity-100"
                          title={t('common.edit')}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {/* Status badge overlay */}
                        {p.accessibility !== 'public' && (
                          <div className="absolute end-2 top-2 z-[3]">
                            <Badge
                              variant={p.accessibility === 'archived' ? 'default' : 'warning'}
                              className="text-[10px] shadow-sm"
                            >
                              {t(`products.${p.accessibility}`, p.accessibility)}
                            </Badge>
                          </div>
                        )}
                      </div>

                      {/* Card body */}
                      <div className="flex flex-1 flex-col p-3">
                        {/* Title -- click to edit inline */}
                        {editingTitle === p._id ? (
                          <input
                            type="text"
                            value={cardEdits.get(p._id)?.title ?? p.title}
                            onChange={(e) => updateCardField(p._id, 'title', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') setEditingTitle(null);
                              if (e.key === 'Escape') {
                                // Revert title edit
                                setCardEdits(prev => {
                                  const next = new Map(prev);
                                  const existing = next.get(p._id);
                                  if (existing) {
                                    const { title: _removed, ...rest } = existing;
                                    if (Object.keys(rest).length === 0) next.delete(p._id);
                                    else next.set(p._id, rest);
                                  }
                                  return next;
                                });
                                setEditingTitle(null);
                              }
                            }}
                            onBlur={() => setEditingTitle(null)}
                            autoFocus
                            className="text-sm font-medium leading-snug rounded-lg border border-border-default bg-bg-primary px-2 py-1 outline-none focus:border-accent w-full"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingTitle(p._id);
                              if (!cardEdits.get(p._id)?.title) {
                                updateCardField(p._id, 'title', p.title);
                              }
                            }}
                            className="line-clamp-2 text-sm font-medium leading-snug text-start cursor-pointer hover:text-accent transition-colors"
                            title={t('common.clickToEdit', 'Click to edit')}
                          >
                            {cardEdits.get(p._id)?.title ?? p.title}
                          </button>
                        )}

                        {/* Price + Quantity in one row */}
                        <div className="mt-2 flex items-center gap-1.5">
                          <div className="relative flex-1 min-w-0">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={cardEdits.get(p._id)?.price ?? p.price}
                              onChange={(e) => updateCardField(p._id, 'price', Number(e.target.value))}
                              className="w-full text-sm ps-2.5 pe-7 py-1.5 rounded-lg border border-border-default bg-bg-primary outline-none focus:border-accent placeholder:text-text-muted/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder={t('common.price')}
                              title={t('common.price')}
                            />
                            <span className="absolute end-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
                              {(!currentStore?.currency || currentStore.currency === 'SAR')
                                ? <SarIcon className="w-3 h-3" />
                                : <span className="text-[10px] font-medium">{currentStore.currency}</span>}
                            </span>
                          </div>
                          {/* Quantity - hidden for code products (quantity = codes count) */}
                          {p.productType !== 'code' && (
                            <div className="relative flex items-center">
                              <input
                                type="number"
                                min="0"
                                value={(() => {
                                  const editQty = cardEdits.get(p._id)?.quantity;
                                  if (editQty !== undefined) return editQty === null ? '' : editQty;
                                  return p.quantity == null || p.quantity === Infinity ? '' : p.quantity;
                                })()}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? null : Number(e.target.value);
                                  updateCardField(p._id, 'quantity', val);
                                }}
                                placeholder="∞"
                                className="w-[4.5rem] text-sm ps-2.5 pe-7 py-1.5 rounded-lg border border-border-default bg-bg-primary outline-none focus:border-accent text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                title={t('common.quantity')}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const currentEdit = cardEdits.get(p._id);
                                  const currentQty = currentEdit?.quantity !== undefined ? currentEdit.quantity : p.quantity;
                                  if (currentQty === null || currentQty === undefined || currentQty === Infinity) {
                                    updateCardField(p._id, 'quantity', 1);
                                  } else {
                                    updateCardField(p._id, 'quantity', null);
                                  }
                                }}
                                className={`absolute end-1 top-1/2 -translate-y-1/2 text-xs font-bold w-5 h-5 flex items-center justify-center rounded transition-colors ${
                                  (() => {
                                    const currentEdit = cardEdits.get(p._id);
                                    const currentQty = currentEdit?.quantity !== undefined ? currentEdit.quantity : p.quantity;
                                    return currentQty === null || currentQty === undefined || currentQty === Infinity;
                                  })()
                                    ? 'text-accent'
                                    : 'text-text-muted hover:text-accent'
                                }`}
                                title={t('products.unlimited', 'Unlimited')}
                              >
                                ∞
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Meta row: type + categories (hidden on mobile) */}
                        <div className="mt-2 hidden sm:flex flex-wrap items-center gap-1.5">
                          {/* Product type (if not physical) */}
                          {p.productType && p.productType !== 'physical' && (
                            <Badge variant="info" className="text-[10px] px-1.5 py-0">
                              {t(`products.${p.productType}`, p.productType)}
                            </Badge>
                          )}

                          {/* Inline category picker */}
                          {(() => {
                            const editedCats = cardEdits.get(p._id)?.categories;
                            const productCats = editedCats ?? p.categories ?? [];
                            const catNames = productCats
                              .map((cId) => categories.find((c) => c._id === cId))
                              .filter(Boolean);
                            const shown = catNames.slice(0, 2);
                            const overflow = catNames.length - 2;
                            const isPickerOpen = categoryPickerOpen === p._id;
                            return (
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setCategoryPickerOpen(isPickerOpen ? null : p._id); }}
                                  className="flex items-center gap-1 rounded px-1 py-0.5 text-text-muted hover:bg-bg-hover hover:text-text-primary transition-colors"
                                  title={t('products.editCategories', 'Edit categories')}
                                >
                                  {shown.length > 0 ? (
                                    <>
                                      {shown.map((c) => (
                                        <Badge key={c!._id} className="text-[10px] px-1.5 py-0">{c!.name}</Badge>
                                      ))}
                                      {overflow > 0 && <span className="text-[10px] text-text-muted">+{overflow}</span>}
                                    </>
                                  ) : (
                                    <span className="text-[10px] text-text-muted flex items-center gap-0.5"><Tag className="h-3 w-3" />{t('products.addCategory', 'Category')}</span>
                                  )}
                                </button>
                                {isPickerOpen && (
                                  <>
                                    <div className="fixed inset-0 z-10" onClick={() => setCategoryPickerOpen(null)} />
                                    <div className="absolute start-0 top-full z-20 mt-1 w-48 max-h-48 overflow-y-auto rounded-lg border border-border-default bg-bg-secondary shadow-xl p-2 space-y-1">
                                      {categories.map((cat) => {
                                        const isSelected = productCats.includes(cat._id);
                                        return (
                                          <button
                                            key={cat._id}
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const newCats = isSelected
                                                ? productCats.filter(id => id !== cat._id)
                                                : [...productCats, cat._id];
                                              updateCardField(p._id, 'categories', newCats);
                                            }}
                                            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors ${
                                              isSelected ? 'bg-accent/10 text-accent font-medium' : 'text-text-secondary hover:bg-bg-hover'
                                            }`}
                                          >
                                            <span className={`h-3.5 w-3.5 rounded border flex items-center justify-center ${isSelected ? 'border-accent bg-accent text-white' : 'border-border-default'}`}>
                                              {isSelected && <Check className="h-2.5 w-2.5" />}
                                            </span>
                                            {cat.name}
                                          </button>
                                        );
                                      })}
                                      {categories.length === 0 && (
                                        <p className="text-xs text-text-muted text-center py-2">{t('categories.empty', 'No categories')}</p>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Spacer */}
                        <div className="flex-1" />

                        {/* Actions row */}
                        <div className="mt-3 flex items-center justify-between border-t border-border-default/50 pt-2.5">
                          <div className="flex items-center gap-1">
                            {/* Save button -- only visible when card has unsaved changes */}
                            {isCardDirty(p._id, p) && (
                              <PermissionGate requires="products.edit">
                                <button
                                  onClick={() => saveCard(p._id)}
                                  disabled={cardSaving.has(p._id)}
                                  className="flex items-center gap-1 text-xs bg-success/10 text-success rounded-lg px-2 py-1 transition-colors hover:bg-success/20 disabled:opacity-50"
                                  title={t('products.save', 'Save')}
                                >
                                  {cardSaving.has(p._id) ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Check className="h-3 w-3" />
                                  )}
                                  <span className="hidden sm:inline">{t('products.save', 'Save')}</span>
                                </button>
                              </PermissionGate>
                            )}
                            {/* Success flash indicator */}
                            {savedCards.has(p._id) && !isCardDirty(p._id, p) && (
                              <span className="flex items-center gap-1 text-xs text-success">
                                <Check className="h-3 w-3" />
                              </span>
                            )}
                            <PermissionGate requires="products.edit">
                              <button
                                onClick={() => setFormProductId(p.productId || p._id)}
                                className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-bg-hover hover:text-text-primary"
                                title={t('common.edit')}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </PermissionGate>
                          </div>
                          <div className="flex items-center">
                            <button
                              onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setOpenMenuId(hasMenu ? null : p._id); }}
                              className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-bg-hover hover:text-text-primary"
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </button>
                            <PortalDropdown
                              anchorEl={menuAnchor}
                              open={hasMenu}
                              onClose={() => setOpenMenuId(null)}
                              width={176}
                              className="w-44 rounded-lg border border-border-default bg-bg-secondary shadow-xl"
                            >
                                  <PermissionGate requires="products.edit">
                                    <button onClick={() => { setFormProductId(p.productId || p._id); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-bg-hover">
                                      <Pencil className="h-4 w-4" /> {t('common.edit')}
                                    </button>
                                  </PermissionGate>
                                  <button onClick={() => { setStatsProduct(p); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-bg-hover">
                                    <BarChart3 className="h-4 w-4" /> {t('products.stats', 'Stats')}
                                  </button>
                                  <button onClick={() => { window.open(`https://${currentStore?.id ?? storeId}.00stores.com/product/${p.productId || p._id}`, '_blank'); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-bg-hover">
                                    <ExternalLink className="h-4 w-4" /> {t('products.viewInStore', 'View in Store')}
                                  </button>
                                  <PermissionGate requires="products.add">
                                    <button onClick={() => { duplicateMutation.mutate(p.productId || p._id); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-bg-hover">
                                      <Copy className="h-4 w-4" /> {t('common.duplicate')}
                                    </button>
                                  </PermissionGate>
                                  {p.paylinkUrl ? (
                                    <button onClick={() => { navigator.clipboard.writeText(p.paylinkUrl!); toast('success', t('common.copied', 'Copied to clipboard')); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-accent hover:bg-bg-hover">
                                      <Copy className="h-4 w-4" /> {t('products.copyPaylink', 'Copy Paylink')}
                                    </button>
                                  ) : (
                                    <PermissionGate requires="paylinks.add">
                                      <button onClick={() => { setPaylinkTarget(p); setPaylinkTitle(p.title); setPaylinkQty(1); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-accent hover:bg-bg-hover">
                                        <Link className="h-4 w-4" /> {t('products.createPaylink')}
                                      </button>
                                    </PermissionGate>
                                  )}
                                  <PermissionGate requires="products.delete">
                                    <button onClick={() => { setDeleteTarget(p); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-bg-hover">
                                      <Trash2 className="h-4 w-4" /> {t('common.delete')}
                                    </button>
                                  </PermissionGate>
                            </PortalDropdown>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Grid Pagination */}
              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    total={total}
                    limit={limit}
                    onPageChange={goToPage}
                  />
                </div>
              )}
            </>
          ) : (
            /* Table View */
            <div className="rounded-xl border border-border-default bg-bg-secondary shadow-sm overflow-hidden">
              <DataTable
                columns={columns}
                data={products}
                isLoading={isLoading}
                keyExtractor={(p) => p._id}
                emptyTitle={t('common.noResults')}
                emptyDescription={t('common.tryDifferentSearch')}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={goToPage}
              />
            </div>
          )}
        </>
      )}

      {/* Mobile FAB */}
      {!isEmpty && (
        <button
          onClick={() => setFormProductId(undefined)}
          className="fixed bottom-6 end-6 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-white shadow-lg transition-transform hover:scale-105 active:scale-95 sm:hidden"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}

      {/* Product form modal lives on DashboardLayout via ProductFormProvider */}

      {/* Image management popup */}
      {imagePopupProduct && (
        <ImageManagePopup
          product={imagePopupProduct}
          storeId={storeId!}
          storeMongoId={currentStore?._id}
          onClose={() => setImagePopupProduct(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['products', storeId] });
            setImagePopupProduct(null);
          }}
        />
      )}

      {/* Product stats modal */}
      {statsProduct && (
        <ProductStatsModal
          isOpen={!!statsProduct}
          onClose={() => setStatsProduct(null)}
          productId={statsProduct.productId || statsProduct._id}
          productTitle={statsProduct.title}
        />
      )}

      {/* Delete confirmation */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        title={t('common.confirmDelete', 'Confirm delete')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={confirmDelete} isLoading={deleting}>
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          {t('products.confirmDeleteMessage', 'Are you sure you want to delete')}{' '}
          <span className="font-medium text-text-primary">{deleteTarget?.title}</span>?
        </p>
      </Modal>

      {/* Quotation Modal */}
      <QuotationModal
        isOpen={showQuotation}
        onClose={() => setShowQuotation(false)}
        storeId={storeId!}
        products={products}
      />

      {/* Per-row Paylink Modal */}
      <Modal
        isOpen={!!paylinkTarget}
        onClose={() => setPaylinkTarget(null)}
        title={t('products.createPaylink')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPaylinkTarget(null)}>{t('common.cancel')}</Button>
            <Button
              isLoading={paylinkMutation.isPending}
              onClick={() => paylinkTarget && paylinkMutation.mutate({ productId: paylinkTarget._id, title: paylinkTitle, qty: paylinkQty })}
            >
              <Link className="h-4 w-4" />
              {t('products.createPaylink')}
            </Button>
          </>
        }
      >
        {paylinkTarget && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg bg-bg-tertiary p-3">
              {paylinkTarget.images?.[0] ? (
                <img src={getCdnUrl(paylinkTarget.images[0], currentStore?._id)} alt="" className="h-12 w-12 rounded-lg object-cover" />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-bg-primary" />
              )}
              <div>
                <p className="font-medium">{paylinkTarget.title}</p>
                <CurrencyDisplay amount={paylinkTarget.price} currency={currentStore?.currency} className="text-sm text-text-secondary" />
              </div>
            </div>
            <Input
              label={t('paylinks.title', 'Payment Link Title')}
              value={paylinkTitle}
              onChange={(e) => setPaylinkTitle(e.target.value)}
            />
            <Input
              label={t('common.quantity')}
              type="number"
              min="1"
              value={paylinkQty}
              onChange={(e) => setPaylinkQty(Math.max(1, Number(e.target.value) || 1))}
            />
          </div>
        )}
      </Modal>

      {/* Quick Category Create Modal */}
      <Modal
        isOpen={showCategoryCreate}
        onClose={() => setShowCategoryCreate(false)}
        title={t('categories.addCategory', 'Add Category')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCategoryCreate(false)}>{t('common.cancel')}</Button>
            <Button
              isLoading={categoryCreateMutation.isPending}
              onClick={() => newCategoryName.trim() && categoryCreateMutation.mutate({ name: newCategoryName.trim(), description: newCategoryDescription.trim() || undefined, parentId: newCategoryParentId || undefined })}
              disabled={!newCategoryName.trim()}
            >
              <FolderPlus className="h-4 w-4" />
              {t('common.create', 'Create')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('categories.categoryName', 'Category Name')}
            placeholder={t('categories.categoryNamePlaceholder', 'e.g. Electronics')}
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newCategoryName.trim()) categoryCreateMutation.mutate({ name: newCategoryName.trim(), description: newCategoryDescription.trim() || undefined, parentId: newCategoryParentId || undefined }); }}
            autoFocus
            required
          />
          <Textarea
            label={t('common.description', 'Description')}
            placeholder={t('categories.descriptionPlaceholder', 'Brief description of this category')}
            value={newCategoryDescription}
            onChange={(e) => setNewCategoryDescription(e.target.value)}
            rows={2}
          />
          {categories.length > 0 && (
            <Select
              label={t('categories.parentCategory', 'Parent Category')}
              options={[
                { value: '', label: t('categories.noParent', 'None (top-level)') },
                ...categories.map((cat) => ({ value: cat._id, label: cat.name })),
              ]}
              value={newCategoryParentId}
              onChange={(e) => setNewCategoryParentId(e.target.value)}
            />
          )}
        </div>
      </Modal>

      {/* Standalone Quick Paylink Modal */}
      <QuickPaylinkModal
        isOpen={showQuickPaylink}
        onClose={() => setShowQuickPaylink(false)}
        storeId={storeId!}
      />

    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Image Management Popup                                             */
/* ------------------------------------------------------------------ */

function ImageManagePopup({
  product,
  storeId,
  storeMongoId,
  onClose,
  onSaved,
}: {
  product: Product;
  storeId: string;
  storeMongoId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  // One ordered list mixing existing filenames and new base64 uploads, so a
  // freshly added image can be moved to the front and become the preview.
  type ImgItem = { id: string; existing: boolean; value: string };
  const [items, setItems] = useState<ImgItem[]>(
    (product.images ?? []).map((f, i) => ({ id: `e${i}-${f}`, existing: true, value: f })),
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Straight to the CDN (see lib/imageUpload); the item value is the returned
  // basename, so nothing is base64'd into the save.
  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    let skipped = 0;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (rejectReasonFor(file)) { skipped++; continue; }
        try {
          const fileName = await productsApi.uploadImage(storeId, file);
          setItems((prev) =>
            prev.length >= MAX_PRODUCT_IMAGES
              ? prev
              : [...prev, { id: `n${Date.now()}-${prev.length}`, existing: true, value: fileName }],
          );
        } catch {
          skipped++;
        }
      }
    } finally {
      setUploading(false);
    }
    if (skipped > 0) {
      toast('error', t('products.imagesSkipped', '{{count}} file(s) skipped: not an image, or larger than 10MB.', { count: skipped }));
    }
  };

  const allImages = items.map((it, i) => ({
    src: it.existing ? getCdnUrl(it.value, storeMongoId) : it.value,
    key: it.id,
    index: i,
  }));

  const moveToFront = (index: number) => {
    setItems((prev) => {
      if (index <= 0) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.unshift(item);
      return next;
    });
  };

  const removeImage = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // The server rejects an oversized body with a bare 413, which surfaced here
    // as the generic error toast. Say what is actually wrong instead.
    if (totalUploadBytes(items.map((it) => it.value)) > MAX_UPLOAD_BYTES) {
      toast('error', t('products.imagesTooLarge', 'These images are too large to save at once. Remove a few and save again.'));
      return;
    }
    setSaving(true);
    try {
      // One ordered array; the backend takes the FIRST item as previewImage
      // (new uploads included) and the rest as image attachments.
      await productsApi.update(storeId, product.productId || product._id, {
        images: items.map((it) => it.value),
      } as any);
      toast('success', t('common.success'));
      onSaved();
    } catch {
      toast('error', t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg rounded-2xl bg-bg-primary border border-border-default shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border-default">
          <h3 className="text-lg font-semibold">{t('common.images')}</h3>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary hover:bg-bg-hover transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {allImages.length > 0 && (
            <p className="text-xs text-text-muted">{t('products.firstIsPreview', 'First image is the preview image')}</p>
          )}

          {/* Image grid */}
          <div className="flex flex-wrap gap-3">
            {allImages.map((img, i) => (
              <div key={img.key} className="relative group">
                <button
                  type="button"
                  onClick={() => {
                    if (i === 0) return; // already first
                    moveToFront(img.index);
                  }}
                  className={`relative h-24 w-24 rounded-xl overflow-hidden border-2 transition-all ${
                    i === 0 ? 'border-accent ring-2 ring-accent/20' : 'border-border-default hover:border-accent/50 cursor-pointer'
                  }`}
                  title={i === 0 ? t('products.previewImage', 'Preview image') : t('products.clickToSetPreview', 'Click to set as preview')}
                >
                  <img src={img.src} alt="" className="h-full w-full object-cover" />
                  {i === 0 && (
                    <div className="absolute top-1 start-1 rounded-full bg-accent p-0.5">
                      <Star className="h-2.5 w-2.5 text-white fill-white" />
                    </div>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => removeImage(img.index)}
                  className="absolute -top-1.5 -end-1.5 hidden group-hover:flex rounded-full bg-danger p-1 text-white hover:bg-danger/80 shadow-md"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}

            {/* Add tile */}
            {allImages.length < MAX_PRODUCT_IMAGES && (
              <label className="flex h-24 w-24 cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-border-default hover:border-accent/50 transition-colors">
                <Plus className="h-6 w-6 text-text-muted" />
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
              </label>
            )}
          </div>

          {allImages.length >= MAX_PRODUCT_IMAGES && (
            <p className="text-xs text-text-muted">
              {t('products.imagesLimitReached', 'Maximum {{count}} images per product.', { count: MAX_PRODUCT_IMAGES })}
            </p>
          )}

          {/* Drag and drop area */}
          {allImages.length < MAX_PRODUCT_IMAGES && (
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              className={`flex h-20 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed transition-colors ${
                dragOver ? 'border-accent bg-accent/5' : 'border-border-default hover:border-accent/50'
              }`}
            >
              <Upload className="h-5 w-5 text-text-muted" />
              <span className="text-xs text-text-muted">{t('products.dragOrClick', 'Drag & drop or click to upload')}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-border-default">
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} isLoading={saving} disabled={uploading}>
            {uploading ? t('products.uploadingImages', 'Uploading...') : t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
